import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Grid,
  Card,
  CardContent,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  IconButton,
  Tooltip,
  TablePagination,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Search as SearchIcon,
  Download as DownloadIcon,
  Science as ScienceIcon,
  ExpandMore as ExpandMoreIcon,
  Description as DocumentIcon,
  Verified as VerifiedIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

const MillTestCertificate = () => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentRecord, setCurrentRecord] = useState({
    mtc_no: '',
    steel_code: '',
    heat_no: '',
    supplier_code: '',
    mtc_date: '',
    test_date: '',
    size: '',
    quantity: '',
    carbon: '',
    manganese: '',
    phosphorus: '',
    sulphur: '',
    silicon: '',
    chromium: '',
    nickel: '',
    molybdenum: '',
    aluminum: '',
    vanadium: '',
    titanium: '',
    copper: '',
    oxygen: '',
    nitrogen: '',
    hydrogen: '',
    tensile_strength: '',
    yield_strength: '',
    elongation: '',
    hardness: '',
    remarks: '',
    verified: false
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [steelCodes, setSteelCodes] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const { api } = useAuth();

  useEffect(() => {
    fetchRecords();
    fetchSteelCodes();
    fetchSuppliers();
  }, []);

  const fetchRecords = async () => {
    try {
      setLoading(true);
      const response = await api.get('/raw-material/mtc');
      setRecords(response.data.data?.mtc || response.data.mtc || []);
    } catch (error) {
      console.error('Error fetching MTC records:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSteelCodes = async () => {
    try {
      const response = await api.get('/masters/steels');
      setSteelCodes(response.data.data || []);
    } catch (error) {
      console.error('Error fetching steel codes:', error);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const response = await api.get('/masters/suppliers');
      setSuppliers(response.data.data || []);
    } catch (error) {
      console.error('Error fetching suppliers:', error);
    }
  };

  const handleAdd = () => {
    setCurrentRecord({
      mtc_no: '',
      steel_code: '',
      heat_no: '',
      supplier_code: '',
      mtc_date: '',
      test_date: '',
      size: '',
      quantity: '',
      carbon: '',
      manganese: '',
      phosphorus: '',
      sulphur: '',
      silicon: '',
      chromium: '',
      nickel: '',
      molybdenum: '',
      aluminum: '',
      vanadium: '',
      titanium: '',
      copper: '',
      oxygen: '',
      nitrogen: '',
      hydrogen: '',
      tensile_strength: '',
      yield_strength: '',
      elongation: '',
      hardness: '',
      remarks: '',
      verified: false
    });
    setEditMode(false);
    setOpenDialog(true);
  };

  const handleEdit = (record) => {
    setCurrentRecord(record);
    setEditMode(true);
    setOpenDialog(true);
  };

  const handleView = (record) => {
    setCurrentRecord(record);
    setOpenViewDialog(true);
  };

  const handleSave = async () => {
    try {
      // Convert empty strings to null for numeric fields
      const data = { ...currentRecord };
      const numericFields = ['carbon', 'manganese', 'phosphorus', 'sulphur', 'silicon', 'chromium', 'nickel', 'molybdenum', 'aluminum', 'vanadium', 'titanium', 'copper', 'oxygen', 'nitrogen', 'hydrogen', 'tensile_strength', 'yield_strength', 'elongation', 'hardness', 'quantity'];
      numericFields.forEach(field => {
        if (data[field] === '' || data[field] === undefined) data[field] = null;
      });
      if (editMode) {
        await api.put(`/raw-material/mtc/${data.mtc_id}`, data);
      } else {
        await api.post('/raw-material/mtc', data);
      }
      fetchRecords();
      setOpenDialog(false);
    } catch (error) {
      console.error('Error saving MTC record:', error);
    }
  };

  const handleExport = () => {
    const csvContent = [
      ['MTC No', 'Steel Code', 'Heat No', 'Supplier', 'Date', 'Size', 'Quantity', 'Carbon %', 'Tensile Strength', 'Status'],
      ...filteredRecords.map(record => [
        record.mtc_no,
        record.steel_code,
        record.heat_no,
        record.supplier_code,
        record.mtc_date,
        record.size || '',
        record.quantity || '',
        record.carbon || '',
        record.tensile_strength || '',
        record.verified ? 'Verified' : 'Pending'
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'mill_test_certificates.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const getVerificationStatus = (verified) => {
    return verified ? {
      label: 'Verified',
      color: 'success',
      icon: <VerifiedIcon />
    } : {
      label: 'Pending',
      color: 'warning',
      icon: <WarningIcon />
    };
  };

  const filteredRecords = records.filter(record =>
    record.tc_no?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    record.heat_no?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    record.mill_code?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const paginatedRecords = filteredRecords.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <ScienceIcon sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4" gutterBottom color="primary">
            Mill Test Certificate
          </Typography>
        </Box>
        <Typography variant="body1" color="textSecondary">
          Raw material chemical composition and mechanical properties certification
        </Typography>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total MTCs
              </Typography>
              <Typography variant="h4">
                {records.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                With Chemical Analysis
              </Typography>
              <Typography variant="h4" color="success.main">
                {records.filter(r => r.carbon && r.manganese).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                With Mechanical Properties
              </Typography>
              <Typography variant="h4" color="warning.main">
                {records.filter(r => r.tensile_strength || r.yield_strength).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Search Results
              </Typography>
              <Typography variant="h4" color="info.main">
                {filteredRecords.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search and Actions */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
        <TextField
          label="Search MTCs..."
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            )
          }}
          sx={{ flexGrow: 1, maxWidth: 400 }}
        />
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={handleExport}
        >
          Export CSV
        </Button>
      </Box>

      {/* Data Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>TC No</strong></TableCell>
                <TableCell><strong>Heat No</strong></TableCell>
                <TableCell><strong>Mill Code</strong></TableCell>
                <TableCell><strong>Carbon %</strong></TableCell>
                <TableCell><strong>Manganese %</strong></TableCell>
                <TableCell><strong>Silicon %</strong></TableCell>
                <TableCell><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedRecords.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="textSecondary">
                      No MTC records found. Add certificates to get started.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedRecords.map((record) => {
                  return (
                    <TableRow key={record.mtc_id} hover>
                      <TableCell>
                        <Chip 
                          label={record.tc_no} 
                          color="primary" 
                          variant="outlined" 
                          size="small"
                          icon={<DocumentIcon />}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                          {record.heat_no}
                        </Typography>
                      </TableCell>
                      <TableCell>{record.mill_code}</TableCell>
                      <TableCell>{record.carbon ? parseFloat(record.carbon).toFixed(3) : '-'}</TableCell>
                      <TableCell>{record.manganese ? parseFloat(record.manganese).toFixed(3) : '-'}</TableCell>
                      <TableCell>{record.silicon ? parseFloat(record.silicon).toFixed(3) : '-'}</TableCell>
                      <TableCell>
                        <Tooltip title="View Details">
                          <IconButton size="small" onClick={() => handleView(record)}>
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => handleEdit(record)}>
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filteredRecords.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(event, newPage) => setPage(newPage)}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
        />
      </Paper>

      {/* Floating Action Button */}
      <Fab
        color="primary"
        aria-label="add"
        sx={{ position: 'fixed', bottom: 16, right: 16 }}
        onClick={handleAdd}
      >
        <AddIcon />
      </Fab>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          {editMode ? 'Edit Mill Test Certificate' : 'Add New MTC'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* Basic Information */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom color="primary">
                Certificate Information
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="MTC Number"
                value={currentRecord.mtc_no}
                onChange={(e) => setCurrentRecord({...currentRecord, mtc_no: e.target.value})}
                disabled={editMode}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Steel Code</InputLabel>
                <Select
                  value={currentRecord.steel_code}
                  label="Steel Code"
                  onChange={(e) => setCurrentRecord({...currentRecord, steel_code: e.target.value})}
                >
                  {steelCodes.map((steel) => (
                    <MenuItem key={steel.steel_code} value={steel.steel_code}>
                      {steel.steel_code} - {steel.steel_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Heat Number"
                value={currentRecord.heat_no}
                onChange={(e) => setCurrentRecord({...currentRecord, heat_no: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Supplier</InputLabel>
                <Select
                  value={currentRecord.supplier_code}
                  label="Supplier"
                  onChange={(e) => setCurrentRecord({...currentRecord, supplier_code: e.target.value})}
                >
                  {suppliers.map((supplier) => (
                    <MenuItem key={supplier.supplier_code} value={supplier.supplier_code}>
                      {supplier.supplier_code} - {supplier.supplier_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="MTC Date"
                type="date"
                value={currentRecord.mtc_date}
                onChange={(e) => setCurrentRecord({...currentRecord, mtc_date: e.target.value})}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Test Date"
                type="date"
                value={currentRecord.test_date}
                onChange={(e) => setCurrentRecord({...currentRecord, test_date: e.target.value})}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Size/Dimension"
                value={currentRecord.size}
                onChange={(e) => setCurrentRecord({...currentRecord, size: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Quantity"
                type="number"
                value={currentRecord.quantity}
                onChange={(e) => setCurrentRecord({...currentRecord, quantity: e.target.value})}
              />
            </Grid>

            {/* Chemical Composition */}
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Divider />
              <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 2 }}>
                <ScienceIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Chemical Composition (%)
              </Typography>
            </Grid>
            
            <Grid item xs={12}>
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>Main Elements</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        fullWidth
                        label="Carbon (%)"
                        type="number"
                        step="0.001"
                        value={currentRecord.carbon}
                        onChange={(e) => setCurrentRecord({...currentRecord, carbon: e.target.value})}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        fullWidth
                        label="Manganese (%)"
                        type="number"
                        step="0.001"
                        value={currentRecord.manganese}
                        onChange={(e) => setCurrentRecord({...currentRecord, manganese: e.target.value})}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        fullWidth
                        label="Silicon (%)"
                        type="number"
                        step="0.001"
                        value={currentRecord.silicon}
                        onChange={(e) => setCurrentRecord({...currentRecord, silicon: e.target.value})}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        fullWidth
                        label="Phosphorus (%)"
                        type="number"
                        step="0.001"
                        value={currentRecord.phosphorus}
                        onChange={(e) => setCurrentRecord({...currentRecord, phosphorus: e.target.value})}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        fullWidth
                        label="Sulphur (%)"
                        type="number"
                        step="0.001"
                        value={currentRecord.sulphur}
                        onChange={(e) => setCurrentRecord({...currentRecord, sulphur: e.target.value})}
                        size="small"
                      />
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            </Grid>

            <Grid item xs={12}>
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>Alloying Elements</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        fullWidth
                        label="Chromium (%)"
                        type="number"
                        step="0.001"
                        value={currentRecord.chromium}
                        onChange={(e) => setCurrentRecord({...currentRecord, chromium: e.target.value})}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        fullWidth
                        label="Nickel (%)"
                        type="number"
                        step="0.001"
                        value={currentRecord.nickel}
                        onChange={(e) => setCurrentRecord({...currentRecord, nickel: e.target.value})}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        fullWidth
                        label="Molybdenum (%)"
                        type="number"
                        step="0.001"
                        value={currentRecord.molybdenum}
                        onChange={(e) => setCurrentRecord({...currentRecord, molybdenum: e.target.value})}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        fullWidth
                        label="Vanadium (%)"
                        type="number"
                        step="0.001"
                        value={currentRecord.vanadium}
                        onChange={(e) => setCurrentRecord({...currentRecord, vanadium: e.target.value})}
                        size="small"
                      />
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            </Grid>

            {/* Mechanical Properties */}
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Divider />
              <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 2 }}>
                Mechanical Properties
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Tensile Strength (MPa)"
                type="number"
                value={currentRecord.tensile_strength}
                onChange={(e) => setCurrentRecord({...currentRecord, tensile_strength: e.target.value})}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Yield Strength (MPa)"
                type="number"
                value={currentRecord.yield_strength}
                onChange={(e) => setCurrentRecord({...currentRecord, yield_strength: e.target.value})}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Elongation (%)"
                type="number"
                step="0.1"
                value={currentRecord.elongation}
                onChange={(e) => setCurrentRecord({...currentRecord, elongation: e.target.value})}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Hardness (HB/HRC)"
                value={currentRecord.hardness}
                onChange={(e) => setCurrentRecord({...currentRecord, hardness: e.target.value})}
                size="small"
              />
            </Grid>

            {/* Additional Information */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Remarks"
                multiline
                rows={3}
                value={currentRecord.remarks}
                onChange={(e) => setCurrentRecord({...currentRecord, remarks: e.target.value})}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Verification Status</InputLabel>
                <Select
                  value={currentRecord.verified}
                  label="Verification Status"
                  onChange={(e) => setCurrentRecord({...currentRecord, verified: e.target.value})}
                >
                  <MenuItem value={false}>Pending Verification</MenuItem>
                  <MenuItem value={true}>Verified</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            {editMode ? 'Update' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={openViewDialog} onClose={() => setOpenViewDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ScienceIcon />
            Mill Test Certificate Details
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom color="primary">
              {currentRecord.mtc_no} - {currentRecord.steel_code}
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">Heat Number</Typography>
                <Typography variant="body2">{currentRecord.heat_no}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">Supplier</Typography>
                <Typography variant="body2">{currentRecord.supplier_code}</Typography>
              </Grid>
              
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">MTC Date</Typography>
                <Typography variant="body2">
                  {currentRecord.mtc_date ? new Date(currentRecord.mtc_date).toLocaleDateString() : '-'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">Status</Typography>
                <Box sx={{ mt: 0.5 }}>
                  <Chip
                    label={getVerificationStatus(currentRecord.verified).label}
                    color={getVerificationStatus(currentRecord.verified).color}
                    size="small"
                    icon={getVerificationStatus(currentRecord.verified).icon}
                  />
                </Box>
              </Grid>
              
              {currentRecord.size && (
                <Grid item xs={6}>
                  <Typography variant="caption" color="textSecondary">Size</Typography>
                  <Typography variant="body2">{currentRecord.size}</Typography>
                </Grid>
              )}
              
              {currentRecord.quantity && (
                <Grid item xs={6}>
                  <Typography variant="caption" color="textSecondary">Quantity</Typography>
                  <Typography variant="body2">{currentRecord.quantity}</Typography>
                </Grid>
              )}
              
              {(currentRecord.carbon || currentRecord.manganese) && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="primary" sx={{ mt: 2 }}>
                    Chemical Composition
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    {currentRecord.carbon && (
                      <Typography variant="body2">Carbon: {currentRecord.carbon}%</Typography>
                    )}
                    {currentRecord.manganese && (
                      <Typography variant="body2">Manganese: {currentRecord.manganese}%</Typography>
                    )}
                    {currentRecord.silicon && (
                      <Typography variant="body2">Silicon: {currentRecord.silicon}%</Typography>
                    )}
                    {currentRecord.phosphorus && (
                      <Typography variant="body2">Phosphorus: {currentRecord.phosphorus}%</Typography>
                    )}
                    {currentRecord.sulphur && (
                      <Typography variant="body2">Sulphur: {currentRecord.sulphur}%</Typography>
                    )}
                  </Box>
                </Grid>
              )}
              
              {(currentRecord.tensile_strength || currentRecord.yield_strength) && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="primary" sx={{ mt: 2 }}>
                    Mechanical Properties
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    {currentRecord.tensile_strength && (
                      <Typography variant="body2">
                        Tensile Strength: {currentRecord.tensile_strength} MPa
                      </Typography>
                    )}
                    {currentRecord.yield_strength && (
                      <Typography variant="body2">
                        Yield Strength: {currentRecord.yield_strength} MPa
                      </Typography>
                    )}
                    {currentRecord.elongation && (
                      <Typography variant="body2">
                        Elongation: {currentRecord.elongation}%
                      </Typography>
                    )}
                    {currentRecord.hardness && (
                      <Typography variant="body2">
                        Hardness: {currentRecord.hardness}
                      </Typography>
                    )}
                  </Box>
                </Grid>
              )}
              
              {currentRecord.remarks && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="textSecondary">Remarks</Typography>
                  <Typography variant="body2">{currentRecord.remarks}</Typography>
                </Grid>
              )}
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenViewDialog(false)}>Close</Button>
          <Button 
            onClick={() => {
              setOpenViewDialog(false);
              handleEdit(currentRecord);
            }} 
            variant="contained"
          >
            Edit
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MillTestCertificate;